// src/utils/crawlFeedAdapter.js
//
// crawl.govglance.org is a separate service from api.govglance.org: different
// host, different filters (country / state / city / level / since), different
// paging (page + size, no skip), different response shape. A feed opts in by
// putting `crawl=recent` in its apiParams; useFeedData checks isCrawlFeed and
// calls fetchCrawlFeed instead of building a main-API request. Items come back
// in the shape FeedItem already renders, so every feed component works on them
// untouched.

const CRAWL_BASE = 'https://crawl.govglance.org/api/v1';

// Filters the crawl service understands. Anything else in apiParams (crawl,
// skip, schema) is ours and never goes into the request.
const CRAWL_FILTERS = ['country', 'state', 'city', 'level', 'since', 'until', 'sort', 'q', 'domain', 'filetype', 'language'];

const parseParams = (apiParams) => new URLSearchParams(String(apiParams || '').replace(/^[&?]/, ''));

export const isCrawlFeed = (apiParams) => parseParams(apiParams).has('crawl');

// path is 'recent' or 'recent.rss' — the RSS variant takes the same filters.
export function crawlUrl(apiParams, { size = 20, page } = {}, path = 'recent') {
  const params = parseParams(apiParams);
  const q = new URLSearchParams();
  CRAWL_FILTERS.forEach((key) => {
    const value = params.get(key);
    if (value) q.set(key, value);
  });
  if (size) q.set('size', String(Math.min(Number(size) || 20, 50)));
  if (page) q.set('page', String(page));
  return `${CRAWL_BASE}/${path}?${q.toString()}`;
}

// title and snippet arrive HTML-escaped and may carry <mark> around matched
// terms. FeedItem renders text, so strip the markup and decode once — decoding
// twice is what turns "apply/renew" into "apply&#x2F;renew".
const decodeEntities = (value) => {
  if (!value) return '';
  if (typeof document === 'undefined') return String(value);
  const el = document.createElement('textarea');
  el.innerHTML = String(value);
  return el.value;
};

const toPlainText = (value) => decodeEntities(String(value || '').replace(/<\/?mark>/gi, '')).trim();

// The index has no row ids of its own; the URL is the stable key.
export function normalizeCrawlItem(result) {
  const source = result.organization || result.domain || '';
  return {
    id: result.url,
    title: toPlainText(result.title) || result.url,
    description: toPlainText(result.snippet),
    summary: toPlainText(result.snippet),
    url: result.url,
    link: result.url,
    source,
    collection_name: source,
    created_at: result.updated_at || result.published_at || result.content_changed_at || null,
    published_at: result.published_at || null,
    updated_at: result.updated_at || null,
    content_changed_at: result.content_changed_at || null,
    domain: result.domain || '',
    level: result.level || null,
    state: result.state || null,
    city: result.city || null,
    isCrawlUpdate: true,
  };
}

// `skip` is how the desktop carousels ask for the Nth item. The crawl service
// pages instead, so the offset is served by slicing one small response rather
// than by a second request.
export async function fetchCrawlFeed(apiParams, limit = 20, signal) {
  const skip = Number(parseParams(apiParams).get('skip')) || 0;
  const count = Number(limit) || 20;

  const res = await fetch(crawlUrl(apiParams, { size: skip + count }), {
    signal,
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    // A city with no indexed government domain is a 400 with suggestions,
    // not an empty result set — keeping the status lets callers tell
    // "nothing indexed here" from "the service is down".
    const detail = await res.json().catch(() => null);
    const err = new Error((detail && detail.detail) || `Crawl API ${res.status}`);
    err.status = res.status;
    throw err;
  }

  const json = await res.json();
  const results = Array.isArray(json.results) ? json.results : [];
  return results.slice(skip, skip + count).map(normalizeCrawlItem);
}